import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import BilingualText from "@/components/BilingualText";
import { useAuth } from "@/lib/auth";
import { IDS } from "@/constants/testIds";

export default function Auth() {
  const [params] = useSearchParams();
  const initialTab = params.get("mode") === "register" ? "register" : "login";
  const [tab, setTab] = useState(initialTab);
  const nav = useNavigate();
  const { login, register, startGuest, loading } = useAuth();

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({
    email: "",
    password: "",
    name: "",
    role: "student",
    age_category: "7-9",
  });

  useEffect(() => { setTab(initialTab); }, [initialTab]);

  const doLogin = async (e) => {
    e.preventDefault();
    try {
      const u = await login(loginForm);
      if (u._migrated) toast.success(`Welcome back! Migrated ${u._migrated} guest answers. 已同步 ${u._migrated} 条本地进度。`);
      else toast.success("Welcome back! 欢迎回来！");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed");
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    try {
      const user = await register(regForm);
      if (user._migrated) toast.success(`Account created! Migrated ${user._migrated} guest answers. 已同步 ${user._migrated} 条本地进度。`);
      else toast.success("Account created! 账号创建成功！");
      nav(user.role === "parent" ? "/parent" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    }
  };

  const doGuest = () => {
    startGuest("7-9");
    nav("/dashboard");
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <Link to="/" className="flex items-center justify-center gap-3">
          <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-sky-400 to-emerald-500 grid place-items-center shadow-md">
            <GraduationCap className="w-5 h-5 text-white" />
          </span>
          <BilingualText cn="剑桥双语英语练习" en="AEIS Bilingual Practice" size="sm" />
        </Link>

        <div className="card-plush p-6 sm:p-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 rounded-full bg-slate-100 p-1 mb-6">
              <TabsTrigger
                value="login"
                data-testid={IDS.auth.tabLogin}
                className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <span className="cn mr-1">登录</span>Sign in
              </TabsTrigger>
              <TabsTrigger
                value="register"
                data-testid={IDS.auth.tabRegister}
                className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <span className="cn mr-1">注册</span>Sign up
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4">
                <div>
                  <Label>Email · 邮箱</Label>
                  <Input
                    type="email"
                    required
                    autoComplete="email"
                    value={loginForm.email}
                    onChange={(e) =>
                      setLoginForm((f) => ({ ...f, email: e.target.value }))
                    }
                    data-testid={IDS.auth.email}
                  />
                </div>
                <div>
                  <Label>Password · 密码</Label>
                  <Input
                    type="password"
                    required
                    autoComplete="current-password"
                    value={loginForm.password}
                    onChange={(e) =>
                      setLoginForm((f) => ({ ...f, password: e.target.value }))
                    }
                    data-testid={IDS.auth.password}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
                  data-testid={IDS.auth.submit}
                >
                  <span className="cn mr-2">登录</span>Sign in
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={doRegister} className="space-y-4">
                <div>
                  <Label>Name · 姓名</Label>
                  <Input
                    required
                    value={regForm.name}
                    onChange={(e) =>
                      setRegForm((f) => ({ ...f, name: e.target.value }))
                    }
                    data-testid={IDS.auth.name}
                  />
                </div>
                <div>
                  <Label>Email · 邮箱</Label>
                  <Input
                    type="email"
                    required
                    autoComplete="email"
                    value={regForm.email}
                    onChange={(e) =>
                      setRegForm((f) => ({ ...f, email: e.target.value }))
                    }
                    data-testid={IDS.auth.email}
                  />
                </div>
                <div>
                  <Label>Password · 密码</Label>
                  <Input
                    type="password"
                    required
                    minLength={6}
                    autoComplete="new-password"
                    value={regForm.password}
                    onChange={(e) =>
                      setRegForm((f) => ({ ...f, password: e.target.value }))
                    }
                    data-testid={IDS.auth.password}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Role · 身份</Label>
                    <Select
                      value={regForm.role}
                      onValueChange={(v) => setRegForm((f) => ({ ...f, role: v }))}
                    >
                      <SelectTrigger data-testid={IDS.auth.role}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="student">Student · 学生</SelectItem>
                        <SelectItem value="parent">Parent · 家长</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {regForm.role === "student" && (
                    <div>
                      <Label>Age · 年龄段</Label>
                      <Select
                        value={regForm.age_category}
                        onValueChange={(v) =>
                          setRegForm((f) => ({ ...f, age_category: v }))
                        }
                      >
                        <SelectTrigger data-testid={IDS.auth.ageCategory}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7-9">7–9 (A2)</SelectItem>
                          <SelectItem value="10-12">10–12 (B1)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
                  data-testid={IDS.auth.submit}
                >
                  <span className="cn mr-2">创建账户</span>Create account
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="pt-6 mt-6 border-t border-slate-100">
            <Button
              variant="outline"
              className="w-full rounded-full font-bold"
              onClick={doGuest}
              data-testid={IDS.auth.guest}
            >
              <span className="cn mr-2">以访客身份继续</span>Continue as guest
            </Button>
            <p className="text-xs text-slate-400 text-center mt-2">
              <span className="cn">进度将保存在本地。</span> Guest progress saves in this browser.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
